import { CircularProgress, TextField } from "@material-ui/core";
import { FormControlLabel } from "@material-ui/core";
import { Checkbox } from "@material-ui/core";
import { Grid } from "@material-ui/core";
import { Button } from "@material-ui/core";
import { Theme, makeStyles } from "@material-ui/core";
import { Snackbar } from "@material-ui/core";
import * as React from "react";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { LazyLoading } from "../../../../../controls/LazyLoading/LazyLoading";
import { Alert } from "../../../../../controls/alert";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { User } from "../../models/User";
import {
	getDataUser,
	setProfilesToUser,
	upsertUser,
} from "../../repositories/UsersRepository";
import { AuthTypeSelector } from "../common/AuthTypeSelector";
import { ProfilesPicker } from "../common/ProfilesPicker";

const useStyles = makeStyles((theme: Theme) => ({
	checkbox: {
		userSelect: "none",
	},
	modalBody: {},
	title: {
		fontSize: 15,
	},
	chip: {
		margin: 2,
	},
	errorLabel: {
		fontSize: 12,
		color: "#F44336",
	},
}));

type Props = {
	userId: number | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const EditUser = ({ userId, show, onHide }: Props) => {
	const classes = useStyles();
	const [open, setOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const [profileError, setprofileError] = useState("");

	const {
		register,
		control,
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<User>({ defaultValues: INITIAL_VALUES });

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};

	const onSubmit: SubmitHandler<User> = async (data) => {
		setIsSubmitLoading(true);
		const res = await upsertUser(data);
		if (res.ok) {
			const userId = res.data.tables[0].rows[0].UserId;
			const resp = await setProfilesToUser(userId, data.ProfilesId as number[]);
			if (resp.ok) {
				onHide(true);
				setOpen(true);
			} else setError(resp.message);
		} else {
			setError(res.message);
		}
	};

	useEffect(() => {
		if (show) {
			setIsSubmitLoading(false);
			(async () => {
				setIsLoading(true);
				const userResponse = await getDataUser(userId);
				if (userResponse.ok) {
					const userSelected = userResponse.data.tables[0].rows[0];
					if (userSelected) {
						const profilesIds = userSelected!.ProfilesId?.split(",") || [];
						setValue("UserId", userSelected!.UserId);
						setValue("UserName", userSelected!.UserName);
						setValue("FirstName", userSelected!.FirstName);
						setValue("LastName", userSelected!.LastName);
						setValue("AuthTypeId", userSelected!.AuthTypeId);
						setValue("IsActive", userSelected!.IsActive);
						setValue("LastLogin", userSelected!.LastLogin);

						setValue(
							"ProfilesId",
							profilesIds.map((pId: string) => parseInt(pId)),
						);
						setIsLoading(false);
					}
				} else setError(userResponse.message);
			})();
		}
	}, [show]);

	return (
		<>
			<MesfModal
				title="EDIT USER"
				open={show}
				handleClose={() => onHide(false)}
				id="edit-user-modal"
				maxWidth="md"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content
						className={classes.modalBody}
						style={{ padding: "15px 30px" }}
					>
						{isLoading && (
							<Grid
								container
								justifyContent="center"
								alignItems="center"
								style={{ minHeight: "300px" }}
							>
								<LazyLoading />
							</Grid>
						)}

						{!isLoading && (
							<>
								<Grid container spacing={1}>
									<Grid item xs={12}>
										<TextField
											{...register("UserName", { required: true })}
											label="Username"
											variant="outlined"
											error={!!errors.UserName}
											fullWidth
											margin="dense"
											autoComplete="off"
										/>
										{errors.UserName?.type === "required" && (
											<span className={classes.errorLabel}>
												User name is required
											</span>
										)}
									</Grid>
									<Grid item md={6} xs={12}>
										<TextField
											{...register("FirstName", { required: true })}
											label="First name"
											variant="outlined"
											error={!!errors.FirstName}
											fullWidth
											margin="dense"
											autoComplete="off"
										/>
										{errors.FirstName?.type === "required" && (
											<span className={classes.errorLabel}>
												First name is required
											</span>
										)}
									</Grid>
									<Grid item md={6} xs={12}>
										<TextField
											{...register("LastName", { required: true })}
											label="Last name"
											variant="outlined"
											error={!!errors.FirstName}
											fullWidth
											margin="dense"
											autoComplete="off"
										/>
										{errors.LastName?.type === "required" && (
											<span className={classes.errorLabel}>
												Last name is required
											</span>
										)}
									</Grid>
									<Grid
										item
										container
										xs={12}
										spacing={3}
										style={{ margin: 0 }}
									>
										<Grid item xs={12} md={6} style={{ padding: "0 15px 0 0" }}>
											<Controller
												name="ProfilesId"
												control={control}
												rules={{ required: true }}
												render={({ field: { onChange, value } }) => (
													<ProfilesPicker
														onChange={onChange}
														value={value as number[]}
														userIdForDelete={null}
														setprofileError={setprofileError}
													/>
												)}
											/>
											{errors.ProfilesId && (
												<span className={classes.errorLabel}>
													{"Select a profile(s)"}
												</span>
											)}
										</Grid>
										<Grid item xs={12} md={6} style={{ padding: "0 0 0 5px" }}>
											<Controller
												name="AuthTypeId"
												control={control}
												render={({ field: { value, onChange } }) => (
													<AuthTypeSelector value={value} onChange={onChange} />
												)}
											/>
										</Grid>
										<Grid item xs={12} md={6}>
											<Controller
												name="IsActive"
												control={control}
												render={({ field }) => (
													<FormControlLabel
														className={classes.checkbox}
														control={
															<Checkbox
																checked={field.value}
																onChange={field.onChange}
																name="IsActive"
																color="primary"
															/>
														}
														label="Active"
													/>
												)}
											/>
										</Grid>
									</Grid>
								</Grid>
							</>
						)}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="default"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid>
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</Button>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
				<Alert severity="info" onClose={handleClose}>
					The user was edited successfully
				</Alert>
			</Snackbar>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Editing User"
			/>
			<ErrorModal
				error={profileError}
				onHide={() => setprofileError("")}
				title="Error Choosing Profile"
			/>
		</>
	);
};
